import { findFraction } from '@/lib/helpers/helpers';

type FormatNumberOptions = {
    round?: boolean;
};

const transformNumberToString = (value: number): string | null => {
    if (value === null || value === undefined || isNaN(value)) return '';

    let stringValue = Math.abs(+value).toString();

    const isScientific = stringValue.includes('e');

    if (isScientific) {
        const numberPart = stringValue.split('e')[0];
        let pow: number = Number(stringValue.split('e')[1]);
        let [intPart, decimals = null] = numberPart.split('.');

        if (!Number.isInteger(+numberPart)) {
            decimals = numberPart.split('.')[1];
        }

        if (pow < 0) {
            pow += intPart.length;
        }

        let decimalsString = '';

        if (pow > 0 && decimals !== null) {
            decimalsString = `${intPart}${decimals || ''}`;
            for (let i = 0; i < pow; i++) {
                decimalsString += '0';
            }
        } else {
            for (let i = 0; i < Math.abs(pow); i++) {
                decimalsString += '0';
            }
            decimalsString = `0.${decimalsString}${intPart}${decimals || ''}`;
        }
        return decimalsString;
    } else {
        return stringValue;
    }
};

const getFractionLength = (num: number): number => {
    const stringValue = transformNumberToString(num);
    if (!stringValue) return 0;
    const parts = stringValue.split('.');
    return parts?.[1]?.length || 0;
};

const truncateFraction = (value: number, fraction: number): number => {
    const multiplier = 10 ** fraction;
    return Math.trunc(value * multiplier) / multiplier;
};

/** Formats a number with `Intl.NumberFormat`; supports optional currency symbol and decimal precision; always returns the absolute value. */
const formatNumber = (
    num: number,
    fraction: number | null = 2,
    currency: string | null = null,
    formatOptions: FormatNumberOptions = {},
): string => {
    const value = Math.abs(+num);
    const shouldRound = formatOptions?.round ?? true;
    const options: Intl.NumberFormatOptions = {
        minimumFractionDigits: 0,
    };
    if (currency) {
        options.style = 'currency';
        options.currency = currency.toUpperCase();
    }
    if (fraction !== null && !isNaN(fraction) && fraction >= 0) {
        options.maximumFractionDigits = fraction;
    }
    if (fraction === null) {
        options.maximumFractionDigits = getFractionLength(value);
    }

    const valueToFormat =
        !shouldRound && fraction !== null && !isNaN(fraction) && fraction >= 0
            ? truncateFraction(value, fraction)
            : value;

    return new Intl.NumberFormat('en-US', options).format(valueToFormat);
};

export type FormatNumberType = typeof formatNumber;

/** Extracts the currency symbol string for a given ISO currency code and locale using Intl.NumberFormat. */
export function getCurrencySymbol(currency = '', locale = 'en-US') {
    if (!currency) {
        return '';
    }

    const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency.toLowerCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    const formatted = formatter.format(0);
    return formatted.replace(/\d/g, '').trim();
}

/** Returns the digit count of the integer part of a number (handles scientific notation). */
export const getIntPartLength = (value: number) => {
    const stringValue = transformNumberToString(value);
    if (!stringValue) return 0;
    const parts = stringValue.split('.');
    return parts?.[0]?.length || 0;
};

/** Computes fixed decimal places so that the total significant digit count equals `maxDigits` (default 5). */
export const getFixedFraction = (value: number, maxDigits?: number) => {
    const fixedLength = maxDigits || 5;
    const intLength = getIntPartLength(value);
    return Math.abs(fixedLength - intLength);
};

type Formatter = 'b' | 'm' | 'k';

type NFormatter = (
    number: number,
    currency?: string,
    formatter?: Formatter[],
    fraction?: number,
    maxFraction?: number,
) => string | number;
/** Formats a number with a B/M/K suffix, preserving sign and currency symbol; uses `findFraction` for precision. */
export const formatBigNumber: NFormatter = (
    number,
    currency = '',
    formatter = [],
    fraction = 2,
    maxFraction = 5,
) => {
    const num = Math.abs(number);
    const sign = number >= 0 ? 1 : -1;
    const currencySymbol = getCurrencySymbol(currency);

    if (number < 1000) {
        return formatNumber(
            number,
            findFraction(number, getFixedFraction(number), maxFraction),
            currency,
        );
    }

    const conditions = {
        k: 1_000,
        m: 1_000_000,
        b: 1_000_000_000,
    };

    let finalConditions = ['b', 'm', 'k'];
    if (formatter.length !== 0) {
        finalConditions = [...formatter];
        finalConditions.sort((a, b) => conditions[b] - conditions[a]);
    }

    let result: string | number = formatNumber(num, fraction, currency);

    for (let i = 0; i < finalConditions.length; i++) {
        if (num >= conditions[finalConditions[i]] || i === finalConditions.length - 1) {
            const tempValue = (num * sign) / conditions[finalConditions[i]];
            result =
                currencySymbol +
                // tempValue.toFixed(findFraction(tempValue, 1, 2)).replace(/\.0$/, '') +
                formatNumber(
                    tempValue,
                    findFraction(tempValue, getFixedFraction(tempValue), maxFraction),
                ) +
                finalConditions[i].toUpperCase();
            break;
        }
    }

    return result;
};

export default formatNumber;
